/**
 * `/verify-email` - token-consume page for customer signup verification.
 *
 * Email sent by signup + `/v1/portal/auth/email-verification/resend` links
 * here with `?token=…`. The page auto-fires
 * `POST /v1/portal/auth/email-verification/consume` on mount and renders
 * the result.
 *
 * Was missing - backend mailer linked to this URL but the SPA had no
 * matching route -> react-router fell through to `<Navigate to="/" />`
 * -> `ProtectedRoute` bounced to /login, leaving the customer unable to
 * verify. Same shape as the password-reset-confirm fix
 * (PasswordResetConfirmPage.tsx).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import {
  clearPendingRedeemCode,
  getPendingRedeemCode,
} from '../lib/redeem-pending';
import { OrbotoLockup } from '../components/OrbotoLockup';
import { LegalFooter } from '../components/LegalFooter';

type State = 'verifying' | 'success' | 'expired' | 'invalid' | 'failed';

interface ConsumeResponse {
  user: {
    // OCP-515 - the server-persisted carry; present even on a device whose
    // sessionStorage never held the code (the cross-device case).
    pending_redeem_code: string | null;
  };
}

export function VerifyEmailPage() {
  const { t } = useTranslation('verify-email');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>('verifying');
  // OCP-264 / OCP-515 - the same-device fast path: the code parked in
  // sessionStorage when the user signed up in THIS browser. Empty after a
  // cross-device verify-link click; the server-provided value below is the
  // device-independent backstop.
  const [sessionCode] = useState<string | null>(() => getPendingRedeemCode());
  // Strict-mode in dev double-renders effects; the consume endpoint is
  // single-use (any second call hits `verification_token_invalid` since
  // the token is wiped on success). Guard with a ref so we only ever
  // fire one POST per page-load.
  const fired = useRef(false);

  // No-token short-circuit - redirect to /login so the customer isn't
  // looking at a confusing "verifying" spinner forever.
  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const res = await api<ConsumeResponse>(
          '/v1/portal/auth/email-verification/consume',
          {
            method: 'POST',
            body: { token },
          },
        );
        // Refresh the shell identity so the verify banner clears + the shell
        // picks up email_verified without a hard reload (OCP-264).
        await refresh();
        // OCP-515 - auto-continue to the claim as one continuous motion.
        // sessionStorage (same-device) wins when present; otherwise fall back
        // to the server-persisted pending code (cross-device: the verify link
        // opened on a different browser where sessionStorage is empty).
        const resolvedCode = sessionCode ?? res.user.pending_redeem_code ?? null;
        if (resolvedCode) {
          clearPendingRedeemCode();
          navigate(`/redeem?code=${encodeURIComponent(resolvedCode)}`, {
            replace: true,
          });
          return;
        }
        setState('success');
      } catch (err) {
        if (err instanceof ApiError) {
          const code =
            err.body && typeof err.body === 'object' && 'error' in err.body
              ? String((err.body as { error: unknown }).error)
              : null;
          if (code === 'verification_token_expired') {
            setState('expired');
            return;
          }
          if (code === 'verification_token_invalid') {
            setState('invalid');
            return;
          }
        }
        setState('failed');
      }
    })();
    // `fired` guards single-execution; refresh/navigate/sessionCode are stable.
  }, [token, refresh, navigate, sessionCode]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">{t('header')}</div>
        </div>

        {state === 'verifying' && (
          <p className="text-sm text-gray-700 dark:text-gray-300">{t('verifying')}</p>
        )}

        {state === 'success' && (
          // OCP-515 - reached only when there is NO pending code to continue to
          // (neither same-device sessionStorage nor the server-side carry). A
          // pending code auto-navigates straight to /redeem?code=X above, so
          // the customer never sees this manual step in the claim flow.
          <div className="flex flex-col gap-4 text-sm text-gray-700 dark:text-gray-300">
            <p>{t('success')}</p>
            <Link
              to="/login"
              className="inline-block rounded bg-brand-500 px-3 py-2 text-center text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
            >
              {t('goToSignIn')}
            </Link>
          </div>
        )}

        {state === 'expired' && (
          <div className="flex flex-col gap-4 text-sm text-gray-700 dark:text-gray-300">
            <div
              role="alert"
              className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-amber-800 dark:text-amber-300"
            >
              {t('expired.title')}
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {t('expired.body')}
            </p>
            <Link
              to="/login"
              className="inline-block rounded bg-brand-500 px-3 py-2 text-center text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
            >
              {t('goToSignIn')}
            </Link>
          </div>
        )}

        {state === 'invalid' && (
          <div className="flex flex-col gap-4 text-sm text-gray-700 dark:text-gray-300">
            <div
              role="alert"
              className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-gray-700 dark:text-gray-300"
            >
              {t('invalid.title')}
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {t('invalid.body')}
            </p>
            <Link
              to="/login"
              className="inline-block rounded bg-brand-500 px-3 py-2 text-center text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
            >
              {t('goToSignIn')}
            </Link>
          </div>
        )}

        {state === 'failed' && (
          <div className="flex flex-col gap-4 text-sm text-gray-700 dark:text-gray-300">
            <div
              role="alert"
              className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-red-700 dark:text-red-300"
            >
              {t('failed')}
            </div>
            <button
              type="button"
              onClick={() => {
                fired.current = false;
                setState('verifying');
                // Trigger the effect again by toggling the ref state path.
                window.location.reload();
              }}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              {t('retry')}
            </button>
            <Link to="/login" className="text-center text-sm text-gray-500 hover:text-brand-500">
              {t('backToSignIn')}
            </Link>
          </div>
        )}
        <LegalFooter className="mt-8" />
      </div>
    </div>
  );
}
